import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Save, Check, X, Package, FileText, AlertCircle, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { grnAPI, purchaseOrdersAPI, vendorsAPI, warehousesAPI, productsAPI } from '@/services/api';

const GRNEntryPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const poIdFromUrl = searchParams.get('po');
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingGRN, setExistingGRN] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  
  // Master data
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // Search states
  const [vendorSearch, setVendorSearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [poSearch, setPoSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showWarehouseDropdown, setShowWarehouseDropdown] = useState(false);
  const [showPODropdown, setShowPODropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  // Refs
  const vendorRef = useRef(null);
  const warehouseRef = useRef(null);
  const poRef = useRef(null);
  const productRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    po_id: '',
    po_number: '',
    vendor_id: '',
    vendor_name: '',
    vendor_code: '',
    warehouse_id: '',
    warehouse_name: '',
    invoice_number: '',
    invoice_date: '',
    notes: '',
    items: [],
    // Additional charges/discounts
    additional_expenses: 0,
    bill_discount: 0,
    round_off: 0,
    adjustment_notes: ''
  });
  
  const [activeTab, setActiveTab] = useState('entry');
  const { toast } = useToast();

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorRef.current && !vendorRef.current.contains(event.target)) setShowVendorDropdown(false);
      if (warehouseRef.current && !warehouseRef.current.contains(event.target)) setShowWarehouseDropdown(false);
      if (poRef.current && !poRef.current.contains(event.target)) setShowPODropdown(false);
      if (productRef.current && !productRef.current.contains(event.target)) setShowProductDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch master data
  const fetchMasterData = useCallback(async () => {
    try {
      const [vendorsRes, warehousesRes, productsRes, posApproved, posPartial] = await Promise.all([
        vendorsAPI.list({ is_active: true }),
        warehousesAPI.list({ is_active: true }),
        productsAPI.list({ is_active: true }),
        purchaseOrdersAPI.list({ status: 'approved' }),
        purchaseOrdersAPI.list({ status: 'partially_received' })
      ]);
      setVendors(vendorsRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setProducts(productsRes.data?.items || productsRes.data || []);
      setPurchaseOrders([...(posApproved.data || []), ...(posPartial.data || [])]);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  }, []);

  // Fetch existing GRN if editing
  const fetchGRN = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await grnAPI.get(id);
      const grn = response.data;
      setExistingGRN(grn);
      setFormData({
        po_id: grn.po_id || '',
        po_number: grn.po_number || '',
        vendor_id: grn.vendor_id,
        vendor_name: grn.vendor_name,
        vendor_code: grn.vendor_code,
        warehouse_id: grn.warehouse_id,
        warehouse_name: grn.warehouse_name,
        invoice_number: grn.invoice_number || '',
        invoice_date: grn.invoice_date ? grn.invoice_date.split('T')[0] : '',
        notes: grn.notes || '',
        // Additional charges/discounts
        additional_expenses: grn.additional_expenses || 0,
        bill_discount: grn.bill_discount || 0,
        round_off: grn.round_off || 0,
        adjustment_notes: grn.adjustment_notes || '',
        items: grn.items.map(item => ({
          po_item_id: item.po_item_id || '',
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          uom: item.uom || 'PCS',
          ordered_qty: item.ordered_qty || 0,
          pending_qty: item.ordered_qty - (item.received_qty || 0),
          received_qty: item.received_qty,
          accepted_qty: item.accepted_qty,
          rejected_qty: item.rejected_qty || 0,
          rejection_reason: item.rejection_reason || '',
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          scheme_discount_percent: item.scheme_discount_percent || 0,
          scheme_discount_amount: item.scheme_discount_amount || 0,
          tax_rate: item.tax_rate || 0,
          mrp: item.mrp || 0,
          batch_number: item.batch_number || '',
          expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : ''
        }))
      });
      setVendorSearch(grn.vendor_name);
      setWarehouseSearch(grn.warehouse_name);
      setPoSearch(grn.po_number || '');
      
      // Fetch vendor details for balance
      const vendorDetail = vendors.find(v => v.id === grn.vendor_id);
      setSelectedVendor(vendorDetail || null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load GRN', variant: 'destructive' });
      navigate('/hq/grn');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast, vendors]);

  // Load PO if coming from PO page
  const loadPOData = useCallback(async (poId) => {
    if (!poId) return;
    try {
      const [poRes, pendingRes] = await Promise.all([
        purchaseOrdersAPI.get(poId),
        grnAPI.getPendingPOItems(poId)
      ]);
      const po = poRes.data;
      const pendingItems = pendingRes.data;
      
      const vendor = vendors.find(v => v.id === po.vendor_id);
      setSelectedVendor(vendor || null);
      
      setFormData(prev => ({
        ...prev,
        po_id: poId,
        po_number: po.po_number,
        vendor_id: po.vendor_id,
        vendor_name: po.vendor_name,
        vendor_code: po.vendor_code,
        warehouse_id: po.warehouse_id,
        warehouse_name: po.warehouse_name,
        items: pendingItems.map(item => ({
          po_item_id: item.item_id || '',
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          uom: item.uom || 'PCS',
          ordered_qty: item.ordered_qty,
          pending_qty: item.pending_qty,
          received_qty: item.pending_qty,
          accepted_qty: item.pending_qty,
          rejected_qty: 0,
          rejection_reason: '',
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          discount_amount: 0,
          scheme_discount_percent: 0,
          scheme_discount_amount: 0,
          tax_rate: item.tax_rate || 0,
          mrp: 0,
          batch_number: '',
          expiry_date: ''
        }))
      }));
      setVendorSearch(po.vendor_name);
      setWarehouseSearch(po.warehouse_name);
      setPoSearch(po.po_number);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load PO items', variant: 'destructive' });
    }
  }, [vendors, toast]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  useEffect(() => {
    if (id) {
      fetchGRN();
    } else if (poIdFromUrl && vendors.length > 0) {
      loadPOData(poIdFromUrl);
    }
  }, [id, poIdFromUrl, fetchGRN, loadPOData, vendors]);

  // Filtered lists
  const filteredVendors = vendors.filter(v => 
    vendorSearch && !formData.po_id && (
      v.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
      v.code.toLowerCase().includes(vendorSearch.toLowerCase())
    )
  ).slice(0, 8);

  const filteredWarehouses = warehouses.filter(w => 
    warehouseSearch && !formData.po_id && (
      w.name.toLowerCase().includes(warehouseSearch.toLowerCase()) ||
      w.code.toLowerCase().includes(warehouseSearch.toLowerCase())
    )
  ).slice(0, 8);

  const filteredPOs = purchaseOrders.filter(po => 
    poSearch && (
      po.po_number.toLowerCase().includes(poSearch.toLowerCase()) ||
      po.vendor_name.toLowerCase().includes(poSearch.toLowerCase())
    )
  ).slice(0, 8);

  const filteredProducts = products.filter(p => 
    productSearch && !formData.po_id && (
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku.toLowerCase().includes(productSearch.toLowerCase())
    )
  ).slice(0, 10);

  // Handlers
  const handlePOSelect = async (po) => {
    await loadPOData(po.id);
    setShowPODropdown(false);
  };

  const handleClearPO = () => {
    setFormData(prev => ({
      ...prev,
      po_id: '',
      po_number: '',
      items: []
    }));
    setPoSearch('');
    setSelectedVendor(null);
  };

  const handleVendorSelect = (vendor) => {
    setFormData(prev => ({
      ...prev,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      vendor_code: vendor.code
    }));
    setSelectedVendor(vendor);
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
  };

  const handleWarehouseSelect = (warehouse) => {
    setFormData(prev => ({
      ...prev,
      warehouse_id: warehouse.id,
      warehouse_name: warehouse.name
    }));
    setWarehouseSearch(warehouse.name);
    setShowWarehouseDropdown(false);
  };

  const handleAddProduct = (product) => {
    const exists = formData.items.find(i => i.product_id === product.id);
    if (exists) {
      toast({ title: 'Info', description: 'Product already added' });
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        po_item_id: '',
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        uom: product.uom_symbol || 'PCS',
        ordered_qty: 0,
        pending_qty: 0,
        received_qty: 1,
        accepted_qty: 1,
        rejected_qty: 0,
        rejection_reason: '',
        unit_price: product.current_price?.cost_price || product.cost_price || 0,
        discount_percent: 0,
        discount_amount: 0,
        scheme_discount_percent: 0,
        scheme_discount_amount: 0,
        tax_rate: product.tax_rate || 18,
        mrp: product.current_price?.mrp || 0,
        batch_number: '',
        expiry_date: ''
      }]
    }));
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const handleUpdateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        
        const isNumericField = ['received_qty', 'accepted_qty', 'rejected_qty', 'unit_price', 'tax_rate', 'mrp', 'discount_percent', 'discount_amount', 'scheme_discount_percent', 'scheme_discount_amount'].includes(field);
        const updated = { ...item, [field]: isNumericField ? (parseFloat(value) || 0) : value };
        
        // Auto-adjust accepted qty
        if (field === 'received_qty') {
          updated.accepted_qty = Math.max(0, updated.received_qty - updated.rejected_qty);
        }
        if (field === 'rejected_qty') {
          updated.accepted_qty = Math.max(0, updated.received_qty - updated.rejected_qty);
        }
        
        return updated;
      })
    }));
  };

  const handleRemoveItem = (index) => {
    if (formData.po_id) {
      toast({ title: 'Info', description: 'Cannot remove items from PO-based GRN' });
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculations
  const calculateItemValues = (item) => {
    const grossAmount = item.accepted_qty * item.unit_price;
    // Apply product discount %
    const afterDiscPercent = grossAmount * (1 - (item.discount_percent || 0) / 100);
    // Apply product discount flat
    const afterDiscAmount = afterDiscPercent - (item.discount_amount || 0);
    // Apply scheme discount %
    const afterSchemePercent = afterDiscAmount * (1 - (item.scheme_discount_percent || 0) / 100);
    // Apply scheme discount flat
    const netAmount = Math.max(0, afterSchemePercent - (item.scheme_discount_amount || 0));
    // Tax
    const tax = netAmount * ((item.tax_rate || 0) / 100);
    const lineTotal = netAmount + tax;
    
    // Calculate purchase profit %
    const mrp = item.mrp || 0;
    const netUnitCost = item.accepted_qty > 0 ? netAmount / item.accepted_qty : 0;
    const purchaseProfitPercent = mrp > 0 && netUnitCost > 0 
      ? ((mrp - netUnitCost) / mrp) * 100 
      : 0;
    
    return {
      grossAmount: grossAmount,
      netAmount: netAmount,
      taxAmount: tax,
      lineTotal: lineTotal,
      purchaseProfitPercent: purchaseProfitPercent
    };
  };

  const calculateItemTotal = (item) => {
    return calculateItemValues(item).lineTotal;
  };

  const calculateTotals = () => {
    let grossAmt = 0;
    let netAmt = 0;
    let taxTotal = 0;
    let totalQty = 0;
    let totalRejected = 0;

    formData.items.forEach(item => {
      const values = calculateItemValues(item);
      grossAmt += values.grossAmount;
      netAmt += values.netAmount;
      taxTotal += values.taxAmount;
      totalQty += item.accepted_qty;
      totalRejected += item.rejected_qty;
    });

    // Apply footer adjustments
    const additionalExpenses = parseFloat(formData.additional_expenses) || 0;
    const billDiscount = parseFloat(formData.bill_discount) || 0;
    const roundOff = parseFloat(formData.round_off) || 0;
    
    const subtotalAfterAdjustments = netAmt + additionalExpenses - billDiscount;
    const grandTotal = subtotalAfterAdjustments + taxTotal + roundOff;

    return {
      grossAmt: grossAmt.toFixed(2),
      netAmt: netAmt.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      additionalExpenses: additionalExpenses.toFixed(2),
      billDiscount: billDiscount.toFixed(2),
      roundOff: roundOff.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
      totalQty,
      totalRejected,
      totalItems: formData.items.length
    };
  };

  const totals = calculateTotals();

  // Save handlers
  const handleSave = async () => {
    if (!formData.vendor_id) {
      toast({ title: 'Validation Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (!formData.warehouse_id) {
      toast({ title: 'Validation Error', description: 'Please select a warehouse', variant: 'destructive' });
      return;
    }
    if (formData.items.length === 0) {
      toast({ title: 'Validation Error', description: 'Add at least one item', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        po_id: formData.po_id || null,
        vendor_id: formData.vendor_id,
        warehouse_id: formData.warehouse_id,
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date ? new Date(formData.invoice_date).toISOString() : null,
        notes: formData.notes,
        // Additional charges/discounts
        additional_expenses: parseFloat(formData.additional_expenses) || 0,
        bill_discount: parseFloat(formData.bill_discount) || 0,
        round_off: parseFloat(formData.round_off) || 0,
        adjustment_notes: formData.adjustment_notes || null,
        items: formData.items.map(item => ({
          po_item_id: item.po_item_id || null,
          product_id: item.product_id,
          received_qty: item.received_qty,
          accepted_qty: item.accepted_qty,
          rejected_qty: item.rejected_qty,
          rejection_reason: item.rejection_reason,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          scheme_discount_percent: item.scheme_discount_percent || 0,
          scheme_discount_amount: item.scheme_discount_amount || 0,
          tax_rate: item.tax_rate,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date ? new Date(item.expiry_date).toISOString() : null
        }))
      };

      let grn;
      if (isEditMode) {
        const response = await grnAPI.update(id, payload);
        grn = response.data;
        toast({ title: 'Success', description: 'GRN updated successfully' });
      } else {
        const response = await grnAPI.create(payload);
        grn = response.data;
        toast({ title: 'Success', description: `GRN ${grn.grn_number} created successfully` });
      }

      navigate('/hq/grn');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    try {
      await grnAPI.complete(id || existingGRN?.id);
      toast({ title: 'Success', description: 'GRN completed. Stock and vendor ledger updated.' });
      setCompleteDialogOpen(false);
      navigate('/hq/grn');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to complete GRN', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/hq/grn')} className="h-10 w-10">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? `GRN: ${existingGRN?.grn_number || ''}` : 'Goods Receipt Note Entry'}
          </h1>
          <p className="text-gray-500">
            {formData.po_id ? `Against PO: ${formData.po_number}` : 'Direct GRN Entry'}
          </p>
        </div>
        {existingGRN && (
          <Badge className={existingGRN.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
            {existingGRN.status?.toUpperCase()}
          </Badge>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Side - Form */}
        <div className="lg:col-span-3 space-y-6">
          {/* Header Info Card */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* PO Search */}
                <div className="space-y-2" ref={poRef}>
                  <Label className="text-gray-700">Purchase Order</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search PO..."
                      value={poSearch}
                      onChange={(e) => {
                        setPoSearch(e.target.value);
                        setShowPODropdown(true);
                      }}
                      onFocus={() => setShowPODropdown(true)}
                      disabled={isEditMode || !!formData.po_id}
                      className="pl-10 bg-gray-50 border-gray-300"
                    />
                    {formData.po_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                        onClick={handleClearPO}
                        disabled={isEditMode}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                    {showPODropdown && filteredPOs.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredPOs.map(po => (
                          <div
                            key={po.id}
                            className="px-4 py-2 hover:bg-teal-50 cursor-pointer"
                            onClick={() => handlePOSelect(po)}
                          >
                            <p className="text-gray-800 font-medium">{po.po_number}</p>
                            <p className="text-gray-500 text-xs">{po.vendor_name} • ₹{po.grand_total?.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Vendor Search */}
                <div className="space-y-2" ref={vendorRef}>
                  <Label className="text-gray-700">Vendor *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search vendor..."
                      value={vendorSearch}
                      onChange={(e) => {
                        setVendorSearch(e.target.value);
                        setShowVendorDropdown(true);
                      }}
                      onFocus={() => setShowVendorDropdown(true)}
                      disabled={!!formData.po_id}
                      className="pl-10 bg-gray-50 border-gray-300"
                    />
                    {showVendorDropdown && filteredVendors.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredVendors.map(v => (
                          <div
                            key={v.id}
                            className="px-4 py-2 hover:bg-teal-50 cursor-pointer"
                            onClick={() => handleVendorSelect(v)}
                          >
                            <p className="text-gray-800">{v.name}</p>
                            <p className="text-gray-500 text-xs">{v.code}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Warehouse Search */}
                <div className="space-y-2" ref={warehouseRef}>
                  <Label className="text-gray-700">Warehouse *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search warehouse..."
                      value={warehouseSearch}
                      onChange={(e) => {
                        setWarehouseSearch(e.target.value);
                        setShowWarehouseDropdown(true);
                      }}
                      onFocus={() => setShowWarehouseDropdown(true)}
                      disabled={!!formData.po_id}
                      className="pl-10 bg-gray-50 border-gray-300"
                    />
                    {showWarehouseDropdown && filteredWarehouses.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredWarehouses.map(w => (
                          <div
                            key={w.id}
                            className="px-4 py-2 hover:bg-teal-50 cursor-pointer"
                            onClick={() => handleWarehouseSelect(w)}
                          >
                            <p className="text-gray-800">{w.name}</p>
                            <p className="text-gray-500 text-xs">{w.code}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* GRN Date */}
                <div className="space-y-2">
                  <Label className="text-gray-700">GRN Date</Label>
                  <Input
                    type="date"
                    value={new Date().toISOString().split('T')[0]}
                    disabled
                    className="bg-amber-50 border-amber-200"
                  />
                </div>
              </div>

              {/* Invoice Info Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                    placeholder="Vendor invoice #"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Invoice Date</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-gray-700">Remarks</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items Entry with Tabs */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <CardHeader className="pb-0 border-b border-gray-100">
                <TabsList className="bg-gray-100">
                  <TabsTrigger value="entry" className="data-[state=active]:bg-white">
                    <FileText className="w-4 h-4 mr-2" /> Entry
                  </TabsTrigger>
                  <TabsTrigger value="verify" className="data-[state=active]:bg-white">
                    <Check className="w-4 h-4 mr-2" /> Verify
                  </TabsTrigger>
                </TabsList>
              </CardHeader>
              
              <CardContent className="pt-4">
                <TabsContent value="entry" className="mt-0 space-y-4">
                  {/* Product Search (only for direct GRN) */}
                  {!formData.po_id && (
                    <div className="space-y-2" ref={productRef}>
                      <Label className="text-gray-700">Add Products</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search products by name, SKU..."
                          value={productSearch}
                          onChange={(e) => {
                            setProductSearch(e.target.value);
                            setShowProductDropdown(true);
                          }}
                          onFocus={() => setShowProductDropdown(true)}
                          className="pl-10 bg-gray-50 border-gray-300"
                        />
                        {showProductDropdown && filteredProducts.length > 0 && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredProducts.map(p => (
                              <div
                                key={p.id}
                                className="px-4 py-2 hover:bg-teal-50 cursor-pointer"
                                onClick={() => handleAddProduct(p)}
                              >
                                <div className="flex justify-between">
                                  <div>
                                    <p className="text-gray-800">{p.name}</p>
                                    <p className="text-gray-500 text-xs">{p.sku}</p>
                                  </div>
                                  <p className="text-teal-600">₹{p.current_price?.cost_price || 0}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Items Table */}
                  {formData.items.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-red-600 text-white">
                            <TableHead className="text-white font-semibold">#</TableHead>
                            {formData.po_id && <TableHead className="text-white font-semibold">PO Qty</TableHead>}
                            <TableHead className="text-white font-semibold">Code</TableHead>
                            <TableHead className="text-white font-semibold">Item Description</TableHead>
                            <TableHead className="text-white font-semibold">Rcvd</TableHead>
                            <TableHead className="text-white font-semibold">Accept</TableHead>
                            <TableHead className="text-white font-semibold">Rate</TableHead>
                            <TableHead className="text-white font-semibold">Disc%</TableHead>
                            <TableHead className="text-white font-semibold">Sch.%</TableHead>
                            <TableHead className="text-white font-semibold">Sch.₹</TableHead>
                            <TableHead className="text-white font-semibold">Tax%</TableHead>
                            <TableHead className="text-white font-semibold">MRP</TableHead>
                            <TableHead className="text-white font-semibold">Profit%</TableHead>
                            <TableHead className="text-white font-semibold text-right">Net Amt</TableHead>
                            {!formData.po_id && <TableHead className="w-10"></TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.items.map((item, index) => {
                            const itemValues = calculateItemValues(item);
                            return (
                            <TableRow key={index} className="border-gray-200">
                              <TableCell className="text-gray-500">{(index + 1) * 10}</TableCell>
                              {formData.po_id && (
                                <TableCell className="text-gray-600 font-medium">{item.pending_qty || item.ordered_qty}</TableCell>
                              )}
                              <TableCell className="text-teal-600 font-medium">{item.product_sku}</TableCell>
                              <TableCell>
                                <p className="text-gray-800 text-sm max-w-[120px] truncate" title={item.product_name}>{item.product_name}</p>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  value={item.received_qty}
                                  onChange={(e) => handleUpdateItem(index, 'received_qty', e.target.value)}
                                  className="bg-amber-50 border-amber-200 h-8 w-16 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  value={item.accepted_qty}
                                  className="bg-emerald-50 border-emerald-200 h-8 w-16 text-sm text-emerald-700"
                                  readOnly
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-20 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.5"
                                  value={item.discount_percent || 0}
                                  onChange={(e) => handleUpdateItem(index, 'discount_percent', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-14 text-sm"
                                  placeholder="0"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.5"
                                  value={item.scheme_discount_percent || 0}
                                  onChange={(e) => handleUpdateItem(index, 'scheme_discount_percent', e.target.value)}
                                  className="bg-purple-50 border-purple-200 h-8 w-14 text-sm"
                                  placeholder="0"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={item.scheme_discount_amount || 0}
                                  onChange={(e) => handleUpdateItem(index, 'scheme_discount_amount', e.target.value)}
                                  className="bg-purple-50 border-purple-200 h-8 w-16 text-sm"
                                  placeholder="0"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={item.tax_rate}
                                  onChange={(e) => handleUpdateItem(index, 'tax_rate', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-14 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  value={item.mrp}
                                  onChange={(e) => handleUpdateItem(index, 'mrp', e.target.value)}
                                  className="bg-gray-50 border-gray-300 h-8 w-16 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <span className={`text-sm font-medium ${itemValues.purchaseProfitPercent >= 20 ? 'text-emerald-600' : itemValues.purchaseProfitPercent >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {itemValues.purchaseProfitPercent.toFixed(1)}%
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-medium text-gray-800">
                                ₹{itemValues.lineTotal.toFixed(2)}
                              </TableCell>
                              {!formData.po_id && (
                                <TableCell>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveItem(index)}>
                                    <X className="w-4 h-4 text-red-500" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                      <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>{formData.po_id ? 'Loading PO items...' : 'Search and add products above'}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="verify" className="mt-0">
                  <div className="text-center py-8 text-gray-500">
                    <Check className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>Verification view - Review all items before completing GRN</p>
                  </div>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>

          {/* Footer Totals & Adjustments */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left side - Additional charges */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Bill Adjustments</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Add. Charges (+)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.additional_expenses}
                        onChange={(e) => setFormData(prev => ({ ...prev, additional_expenses: e.target.value }))}
                        className="bg-amber-50 border-amber-200 h-8 text-sm"
                        placeholder="Freight, etc."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Bill Discount (-)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.bill_discount}
                        onChange={(e) => setFormData(prev => ({ ...prev, bill_discount: e.target.value }))}
                        className="bg-emerald-50 border-emerald-200 h-8 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Round Off (+/-)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.round_off}
                        onChange={(e) => setFormData(prev => ({ ...prev, round_off: e.target.value }))}
                        className="bg-gray-50 border-gray-300 h-8 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Adjustment Notes</Label>
                    <Input
                      value={formData.adjustment_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, adjustment_notes: e.target.value }))}
                      placeholder="Reason for additional charges/discount..."
                      className="bg-gray-50 border-gray-300 h-8 text-sm"
                    />
                  </div>
                </div>
                
                {/* Right side - Summary */}
                <div className="space-y-2 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-800 border-b border-amber-200 pb-2">Bill Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Items / Qty:</span>
                      <span className="font-medium">{totals.totalItems} items / {totals.totalQty} qty</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Gross Amount:</span>
                      <span>₹{totals.grossAmt}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Net Amount (after disc):</span>
                      <span>₹{totals.netAmt}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>GST:</span>
                      <span>₹{totals.taxTotal}</span>
                    </div>
                    {parseFloat(totals.additionalExpenses) > 0 && (
                      <div className="flex justify-between text-amber-700">
                        <span>+ Additional Charges:</span>
                        <span>₹{totals.additionalExpenses}</span>
                      </div>
                    )}
                    {parseFloat(totals.billDiscount) > 0 && (
                      <div className="flex justify-between text-emerald-700">
                        <span>- Bill Discount:</span>
                        <span>₹{totals.billDiscount}</span>
                      </div>
                    )}
                    {parseFloat(totals.roundOff) !== 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Round Off:</span>
                        <span>₹{totals.roundOff}</span>
                      </div>
                    )}
                    {totals.totalRejected > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Rejected Qty:</span>
                        <span>{totals.totalRejected}</span>
                      </div>
                    )}
                    <hr className="border-amber-300 my-2" />
                    <div className="flex justify-between text-lg font-bold text-amber-900">
                      <span>Total Payable:</span>
                      <span className="text-teal-600">₹{totals.grandTotal}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Summary & Actions */}
        <div className="space-y-6">
          {/* Vendor Info with Advance */}
          {selectedVendor && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <CardTitle className="text-base text-gray-800 flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-teal-500" />
                  Vendor Account
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div>
                  <p className="text-gray-500 text-xs">Vendor</p>
                  <p className="text-gray-800 font-medium">{selectedVendor.name}</p>
                  <p className="text-teal-600 text-sm">{selectedVendor.code}</p>
                </div>
                <hr className="border-gray-200" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500 text-xs">Opening Balance</p>
                    <p className="text-gray-800 font-medium">₹{(selectedVendor.opening_balance || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Current Balance</p>
                    <p className={`font-bold ${selectedVendor.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      ₹{Math.abs(selectedVendor.current_balance || 0).toLocaleString()}
                      <span className="text-xs font-normal ml-1">{selectedVendor.current_balance > 0 ? '(Dr)' : selectedVendor.current_balance < 0 ? '(Cr)' : ''}</span>
                    </p>
                  </div>
                </div>
                {selectedVendor.current_balance < 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                    <p className="text-emerald-700 text-sm flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Advance Available: ₹{Math.abs(selectedVendor.current_balance).toLocaleString()}
                    </p>
                  </div>
                )}
                {selectedVendor.current_balance > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-2">
                    <p className="text-red-700 text-sm flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Outstanding: ₹{selectedVendor.current_balance.toLocaleString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Card className="bg-white border-gray-200 shadow-sm sticky top-6">
            <CardContent className="pt-6 space-y-3">
              <Button
                className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                onClick={handleSave}
                disabled={saving || existingGRN?.status === 'completed'}
                data-testid="save-grn-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save GRN'}
              </Button>
              
              {(isEditMode || existingGRN) && existingGRN?.status === 'draft' && (
                <Button
                  variant="outline"
                  className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => setCompleteDialogOpen(true)}
                  data-testid="complete-grn-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Complete GRN
                </Button>
              )}
              
              <Button
                variant="ghost"
                className="w-full text-gray-500"
                onClick={() => navigate('/hq/grn')}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Complete Confirmation Dialog */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Complete GRN?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will mark the GRN as completed and:
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>Update warehouse inventory with accepted quantities</li>
                <li>Create vendor ledger entry (payable amount: ₹{totals.grandTotal})</li>
                {formData.po_id && <li>Update PO received status</li>}
              </ul>
              <p className="mt-2 font-medium text-red-600">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              Complete GRN
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GRNEntryPage;
